import React, { useMemo, useState } from "react";
import { Layers, ChevronDown, ChevronRight, Edit3, Trash2, MapPin } from "lucide-react";
import { imageUrl } from "@/lib/api";
import { DepartmentBadge } from "@/components/DepartmentChips";

/**
 * GroupedAssetsView — collapses near-identical assets into a single tile.
 *
 * Why this exists:
 *   Bulk imports (Snap-to-Import with Qty=47 on "Dell Monitor") create 47
 *   individual records named "Dell Monitor (1/47) … (47/47)". Great for
 *   per-unit tracking, terrible for scanning the register — 47 near-identical
 *   cards drowning out everything else. Grouped view shows one tile per
 *   fingerprint with a big "×47" count, location breakdown, and an
 *   expand-to-see-units toggle.
 *
 * Fingerprinting rule (must be deterministic + case-insensitive):
 *   - Strip trailing " (i/N)" bulk-import suffix from the name
 *   - Join stemmed name + brand + model + category, lower-case, single-space
 *   - Items with the same fingerprint stack together
 *
 * Design notes:
 *   - Never hides serials or per-unit condition — the expand rows show them
 *   - Preserves Edit and Delete actions per unit
 *   - Groups with only 1 unit render as a normal single card so the UI
 *     doesn't look weirdly boxed for lone items
 */

const stripQtySuffix = (name = "") =>
  name.replace(/\s*\(\d+\s*\/\s*\d+\)\s*$/, "").trim();

const fingerprint = (a) =>
  [
    stripQtySuffix(a.name || "").toLowerCase(),
    (a.brand || "").toLowerCase(),
    (a.model || "").toLowerCase(),
    (a.category || "").toLowerCase(),
  ]
    .join("|")
    .replace(/\s+/g, " ");

const summariseLocations = (items) => {
  const counts = {};
  for (const it of items) {
    const key = (it.location || "").trim() || "—";
    counts[key] = (counts[key] || 0) + 1;
  }
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);
};

const GroupedAssetsView = ({ items, isManager, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState({});

  const groups = useMemo(() => {
    const map = new Map();
    for (const a of items) {
      const k = fingerprint(a);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(a);
    }
    return Array.from(map.entries())
      .map(([key, units]) => ({
        key,
        units,
        name: stripQtySuffix(units[0].name),
        brand: units[0].brand,
        model: units[0].model,
        category: units[0].category,
        image: units.find((u) => u.image_paths?.[0])?.image_paths?.[0],
        locations: summariseLocations(units),
      }))
      .sort((a, b) => b.units.length - a.units.length);
  }, [items]);

  if (!items?.length) {
    return (
      <div
        className="nb-card p-8 text-center text-sm text-[#607087]"
        data-testid="grouped-assets-empty"
      >
        No assets match your filters.
      </div>
    );
  }

  return (
    <div
      className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
      data-testid="grouped-assets-grid"
    >
      {groups.map((g) => {
        const isOpen = !!expanded[g.key];
        const isStack = g.units.length > 1;
        return (
          <div
            key={g.key}
            data-testid={`grouped-asset-card-${g.key}`}
            className="nb-card p-4 flex flex-col gap-3"
          >
            <div className="flex items-start gap-3">
              {g.image ? (
                <img
                  src={imageUrl(g.image)}
                  alt=""
                  className="w-16 h-16 rounded-lg border-2 border-black object-cover shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg border-2 border-black bg-[#F4F8FB] shrink-0 flex items-center justify-center text-[#001740]/30">
                  <Layers size={24} strokeWidth={2} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg leading-tight truncate">
                  {g.name}
                </div>
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  {isStack && (
                    <span
                      className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black bg-[#001740] text-white font-mono-num"
                      data-testid={`grouped-asset-count-${g.key}`}
                    >
                      × {g.units.length}
                    </span>
                  )}
                  {g.category && (
                    <span className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black bg-[#F1E7FA] text-[#5B3A99]">
                      {g.category}
                    </span>
                  )}
                  {(g.brand || g.model) && (
                    <span className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black bg-white text-[#607087]">
                      {[g.brand, g.model].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {isStack && (
              <div className="text-xs">
                <div className="label-eyebrow text-[#607087] flex items-center gap-1 mb-1">
                  <MapPin size={10} strokeWidth={2.5} /> Where they are
                </div>
                <div className="flex flex-wrap gap-1">
                  {g.locations.map(([loc, n]) => (
                    <span
                      key={loc}
                      className="px-1.5 py-0.5 rounded border border-black/20 bg-[#F4F8FB]"
                    >
                      {loc} <b className="font-mono-num">({n})</b>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!isStack && (
              // Single-unit group — surface the lone unit's key details so
              // the card carries the same weight as an ungrouped card.
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                {g.units[0].location && (
                  <div>
                    <div className="label-eyebrow text-[#607087]">Location</div>
                    <div className="truncate">{g.units[0].location}</div>
                  </div>
                )}
                <div>
                  <div className="label-eyebrow text-[#607087]">Department</div>
                  <DepartmentBadge department={g.units[0].department} />
                </div>
                {g.units[0].serial && (
                  <div className="col-span-2">
                    <div className="label-eyebrow text-[#607087]">Serial</div>
                    <div className="font-mono-num truncate">{g.units[0].serial}</div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between pt-1 border-t border-black/10">
              {isStack ? (
                <button
                  data-testid={`grouped-asset-expand-${g.key}`}
                  onClick={() =>
                    setExpanded((e) => ({ ...e, [g.key]: !e[g.key] }))
                  }
                  className="text-xs font-bold text-[#001740] flex items-center gap-1"
                >
                  {isOpen ? (
                    <ChevronDown size={14} strokeWidth={2.5} />
                  ) : (
                    <ChevronRight size={14} strokeWidth={2.5} />
                  )}
                  {isOpen ? "Hide units" : `Show ${g.units.length} units`}
                </button>
              ) : (
                <div />
              )}
              {isManager && !isStack && (
                <div className="flex gap-1">
                  <button
                    data-testid={`grouped-asset-edit-${g.units[0].id}`}
                    onClick={() => onEdit(g.units[0])}
                    className="nb-btn bg-white px-2 py-1 text-xs flex items-center gap-1"
                  >
                    <Edit3 size={11} strokeWidth={2.5} /> Edit
                  </button>
                  <button
                    data-testid={`grouped-asset-delete-${g.units[0].id}`}
                    onClick={() => onDelete(g.units[0])}
                    className="nb-btn bg-white px-2 py-1 text-xs flex items-center gap-1 text-[#8A1F1F] border-[#8A1F1F]/40"
                  >
                    <Trash2 size={11} strokeWidth={2.5} />
                  </button>
                </div>
              )}
            </div>

            {isStack && isOpen && (
              <div
                className="border-t border-black/10 pt-2 space-y-1 max-h-72 overflow-y-auto"
                data-testid={`grouped-asset-units-${g.key}`}
              >
                {g.units.map((u) => (
                  <div
                    key={u.id}
                    data-testid={`grouped-asset-unit-${u.id}`}
                    className="flex items-center gap-2 text-xs py-1.5 px-2 hover:bg-[#F4F8FB] rounded"
                  >
                    <div className="flex-1 min-w-0 truncate">
                      <b className="font-mono-num text-[#001740]">
                        {u.name.match(/\((\d+)\/\d+\)$/)?.[1] || "1"}
                      </b>
                      {u.location ? ` · ${u.location}` : ""}
                      {u.serial ? ` · SN ${u.serial}` : ""}
                      {u.assignee_text ? ` · ${u.assignee_text}` : ""}
                    </div>
                    {isManager && (
                      <div className="flex gap-1">
                        <button
                          data-testid={`grouped-unit-edit-${u.id}`}
                          onClick={() => onEdit(u)}
                          className="p-1 rounded hover:bg-white"
                          aria-label={`edit unit ${u.name}`}
                        >
                          <Edit3 size={11} strokeWidth={2.5} />
                        </button>
                        <button
                          data-testid={`grouped-unit-delete-${u.id}`}
                          onClick={() => onDelete(u)}
                          className="p-1 rounded hover:bg-white text-[#8A1F1F]"
                          aria-label={`delete unit ${u.name}`}
                        >
                          <Trash2 size={11} strokeWidth={2.5} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GroupedAssetsView;
